<template>
  <v-dialog v-model="dialog"  max-width="500">
    <v-card>
      <v-card-title class="pb-7" > 
        <div class="headline font-weight-bold font--text">Create New Access Key</div>
      </v-card-title>
      <v-card-text>
        <p class="font-weight-bold">Access Key Name</p>
        <v-text-field
          v-model="name"
          outlined
          placeholder="Give your access key a name"
          :error-messages="createErrorMessage('name')"
        />
      </v-card-text>
      <v-card-text>
        <p class="font-weight-bold">Select User</p>
        <v-autocomplete
          :search-input.sync="searchUser"
          v-model="user"
          :items="listMembers"
          outlined
          autocomplete="off"
          item-text="fullname"
          item-value="id"
          placeholder="Select User"
          clearable
          hide-details
          @focus="
            () => {
              if (role == 'Superadmin') superadminFetchMembers({ search: searchUser, page:1, itemsPerPage:999 })
              else fetchMembers({ search: searchUser, page:1, itemsPerPage:999 });
            }
          "
          :error-messages="createErrorMessage('user')"
        >
          <template v-slot:append-outer>
            <div id="filterUser"></div>
          </template>
        </v-autocomplete>
      </v-card-text>
      <v-card-text>
        <p class="font-weight-bold">Expires</p>
        <v-row>
          <v-col cols="6">
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="expiresDate"
                  outlined
                  placeholder="Select Date (optional)"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  clearable
                  @click:clear="clearExpires"
                  hide-details
                />
              </template>
              <v-date-picker
                v-model="expiresDate"
                @input="dateMenu = false"
                :min="minDate"
              />
            </v-menu>
          </v-col>
          <v-col cols="6">
            <v-menu
              v-model="timeMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="expiresTime"
                  outlined
                  placeholder="Select Time (optional)"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  clearable
                  @click:clear="expiresTime = null"
                  hide-details
                  :disabled="!expiresDate"
                />
              </template>
              <v-time-picker
                v-model="expiresTime"
                @click:minute="timeMenu = false"
                format="24hr"
                :min="minTime"
              />
            </v-menu>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-row class="ma-2">
          <v-col cols="6">
            <v-btn depressed
              height="50"
              color="accent"
              block
              @click="
                () => {
                  $emit('close')
                }
              "
            >
              Cancel
            </v-btn>
          </v-col>
          <v-col cols="6">
            <v-btn depressed
              height="50"
              color="secondary"
              block
              @click="create"
            >
              Confirm
            </v-btn>
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>

import { useObjectStorage } from './composableapi/useobjectstorage'
import { ref, watch, computed } from '@vue/composition-api'
import { useMember } from "../organization/usemember";
const { required } = require("@vuelidate/validators");
const useVuelidate = require("@vuelidate/core").default;

export default {
  props: ['value', 'objectStorageID'],
  setup(props, context){
    const Composable = {
      ...useObjectStorage()
    }

    const {
      createAccessKey
    } = Composable
    
    const name = ref('')
    const searchUser = ref('')
    const user = ref(null)
    const expiresDate = ref(null)
    const expiresTime = ref(null)
    const dateMenu = ref(false)
    const timeMenu = ref(false)

    const getTodayDate = () => new Date().toISOString().substr(0, 10)
    const minDate = getTodayDate()

    const minTime = computed(() => {
      if (expiresDate.value === getTodayDate()) {
        const now = new Date()
        const hours = String(now.getHours()).padStart(2, '0')
        const minutes = String(now.getMinutes()).padStart(2, '0')
        return `${hours}:${minutes}`
      }
      return null
    })

    // Reset time if it's less than minTime when date changes to today
    watch(expiresDate, (newDate) => {
      if (newDate === getTodayDate() && expiresTime.value) {
        const now = new Date()
        const [selectedHour, selectedMinute] = expiresTime.value.split(':').map(Number)
        const currentHour = now.getHours()
        const currentMinute = now.getMinutes()

        if (selectedHour < currentHour || (selectedHour === currentHour && selectedMinute < currentMinute)) {
          expiresTime.value = null
        }
      }
    })

    const $v = useVuelidate({
      user: { required },
      name: { required },
    }, {
      user,
      name
    })

    // watch(searchUser, val => {
    //   fetchMembers({ search: searchUser.value })
    // })

    const clearExpires = () => {
      expiresDate.value = null
      expiresTime.value = null
    }

    const getExpiresFormatted = () => {
      if (!expiresDate.value) return null
      const time = expiresTime.value || '00:00'
      // Format: YYYY-MM-DD hh:mm:ss
      return `${expiresDate.value} ${time}:00`
    }

    const resetForm = () => {
      name.value = ''
      user.value = null
      searchUser.value = ''
      expiresDate.value = null
      expiresTime.value = null
      $v.value.$reset()
    }

    const create = async () => {
      $v.value.$touch()
      if($v.value.$invalid) return

      const payload = {
        user_id: user.value,
        name: name.value,
        object_storage_id: props.objectStorageID,
        expires: getExpiresFormatted(),
      }

      const response = await createAccessKey(payload)
      if(response.status == 200) {
        resetForm()
        context.emit('show', response.data.data)
      }

    }

    const createErrorMessage = (element) => {
      return ($v.value[element].$errors.length) ? $v.value[element].$errors[0].$message : ''
    }

    const role = localStorage.getItem('role')

    return {
      role,
      ...Composable,
      searchUser,
      name,
      user,
      create,
      createErrorMessage,
      expiresDate,
      expiresTime,
      dateMenu,
      timeMenu,
      minDate,
      minTime,
      clearExpires,
    }
  },
  created(){
    if (this.role == 'Superadmin') this.superadminFetchMembers({ page:1, itemsPerPage:999 })
    else this.fetchMembers({ page:1, itemsPerPage:999 })
  },
  computed:{
    dialog:{
      get:function(){
        return this.value
      },
      set:function(val){
        this.$emit('close',val)
      }
    }
  }
};
</script>
<style scoped lang="scss">
button{
  ::v-deep .v-btn__content{
  font-size: 14px;
}
}

</style>